import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Clock, XCircle, FileText, Upload, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  not_submitted: { color: "#9a9a9a", icon: <FileText className="w-5 h-5" />, label: "Non soumis" },
  pending: { color: "#F4B400", icon: <Clock className="w-5 h-5" />, label: "En cours d'examen" },
  approved: { color: "#11783A", icon: <CheckCircle2 className="w-5 h-5" />, label: "Validé" },
  rejected: { color: "#D6202C", icon: <XCircle className="w-5 h-5" />, label: "Refusé" },
};

export default function Kyc() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [kyc, setKyc] = useState(null);
  const [rcFile, setRcFile] = useState(null);
  const [taxFile, setTaxFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "pro") {
      toast.error("Vérification KYC réservée aux comptes Pro");
      navigate("/dashboard");
      return;
    }
    (async () => {
      const { data } = await api.get("/kyc/status");
      setKyc(data);
    })();
  }, [user, loading, navigate]);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rcFile || !taxFile) {
      toast.error("Veuillez fournir les 2 documents requis");
      return;
    }
    setBusy(true);
    try {
      const [rc, tax] = await Promise.all([uploadFile(rcFile), uploadFile(taxFile)]);
      const { data } = await api.post("/kyc/submit", {
        rc_file_id: rc.id,
        tax_file_id: tax.id,
        notes: notes || null,
      });
      setKyc(data);
      toast.success("Documents envoyés ✓ Votre dossier est en cours d'examen");
      setRcFile(null);
      setTaxFile(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  const status = kyc?.status || "not_submitted";
  const meta = STATUS_META[status] || STATUS_META.not_submitted;

  return (
    <Layout withFooter={false}>
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-8">
        <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Vérification Pro</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2">Documents KYC</h1>
        <p className="text-muted-foreground mb-8">
          Pour obtenir le badge <strong>Pro vérifié</strong> et instaurer la confiance auprès des acheteurs, fournissez ces deux documents officiels de votre agence.
        </p>

        {/* Status banner */}
        <div
          data-testid="kyc-status-banner"
          className="rounded-2xl border p-5 mb-8 flex items-start gap-4"
          style={{ borderColor: meta.color + "40", background: meta.color + "0D" }}
        >
          <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: meta.color, color: "#fff" }}>
            {meta.icon}
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-medium" style={{ color: meta.color }}>{meta.label}</div>
            {status === "pending" && (
              <div className="text-sm text-muted-foreground mt-1">
                Soumis le {new Date(kyc.submitted_at).toLocaleDateString("fr-FR")}. Notre équipe examine généralement les dossiers sous 24-48 h ouvrées.
              </div>
            )}
            {status === "approved" && (
              <div className="text-sm text-muted-foreground mt-1">
                Félicitations ! Le badge <strong>Pro vérifié</strong> apparaît sur toutes vos annonces.
              </div>
            )}
            {status === "rejected" && kyc.review_notes && (
              <div className="text-sm mt-1" style={{ color: meta.color }}>
                Motif : {kyc.review_notes}. Vous pouvez resoumettre de nouveaux documents.
              </div>
            )}
            {status === "not_submitted" && (
              <div className="text-sm text-muted-foreground mt-1">
                Aucun dossier en cours. Téléversez les 2 documents ci-dessous pour démarrer.
              </div>
            )}
          </div>
        </div>

        {(status === "not_submitted" || status === "rejected") && (
          <form onSubmit={submit} className="bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-5">
            <FileField
              label="Registre du Commerce (RC)"
              hint="PDF ou image — extrait récent (moins de 3 mois)"
              file={rcFile}
              onChange={setRcFile}
              testId="kyc-rc-input"
            />
            <FileField
              label="Attestation fiscale"
              hint="PDF ou image — attestation de régularité fiscale"
              file={taxFile}
              onChange={setTaxFile}
              testId="kyc-tax-input"
            />
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mt-1 rounded-xl"
                placeholder="Précisions à l'attention de notre équipe..."
                data-testid="kyc-notes"
              />
            </div>

            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-[#FEF2F3] border border-[#D6202C]/20 rounded-xl p-3">
              <AlertTriangle className="w-4 h-4 text-[#D6202C] shrink-0 mt-0.5" />
              <span>Documents traités confidentiellement. Notre équipe les examine puis les supprime de manière sécurisée.</span>
            </div>

            <Button
              type="submit"
              disabled={busy || !rcFile || !taxFile}
              data-testid="kyc-submit-btn"
              className="w-full h-12 rounded-xl bg-[#D6202C] hover:bg-[#B5181F]"
            >
              <ShieldCheck className="w-4 h-4 me-2" />
              {busy ? "Envoi en cours..." : "Soumettre mon dossier"}
            </Button>
          </form>
        )}

        {status === "pending" && kyc && (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6 space-y-3">
            <h3 className="font-heading text-lg font-medium">Documents soumis</h3>
            <a href={`${BACKEND_URL}${kyc.rc_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#D6202C] hover:underline">
              <FileText className="w-4 h-4" /> Registre du Commerce
            </a>
            <a href={`${BACKEND_URL}${kyc.tax_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#D6202C] hover:underline">
              <FileText className="w-4 h-4" /> Attestation fiscale
            </a>
            {kyc.notes && (
              <p className="text-xs text-muted-foreground border-t pt-3">Vos notes : {kyc.notes}</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

const FileField = ({ label, hint, file, onChange, testId }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1.5 border-2 border-dashed border-[#E5E5E5] hover:border-[#D6202C] rounded-xl p-4 transition-colors">
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testId}
        className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#FEF2F3] file:text-[#D6202C] hover:file:bg-[#FEE2E5] cursor-pointer"
      />
      {file && (
        <div className="mt-2 flex items-center gap-2 text-xs text-[#11783A]">
          <Upload className="w-3 h-3" /> {file.name} ({(file.size / 1024).toFixed(0)} Ko)
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-2">{hint} · PDF, JPG, PNG ou WEBP · 5 Mo max</p>
    </div>
  </div>
);
